'use client';

import React, { useState, useCallback } from 'react';
import {
  FileIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  UploadIcon,
  PauseIcon,
  PlayIcon,
  Cross2Icon,
  ReloadIcon,
} from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Text } from '../text';
import { Progress } from '../progress';
import { Stack } from '../stack';
import { Button } from '../button';
import { Icon } from '../icon';
import { Badge } from '../badge';

import { FileDropzone } from './file-dropzone';

export interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'paused' | 'completed' | 'error';
  error?: string;
  uploadedSize?: number;
  url?: string;
}

export interface MultiFileUploaderProps extends BoxProps {
  /** Upload handler function */
  onUpload: (file: File, onProgress: (progress: number) => void) => Promise<{ url: string }>;
  /** Accepted file types */
  accept?: Record<string, string[]>;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Maximum number of files */
  maxFiles?: number;
  /** Whether to upload automatically after selection */
  autoUpload?: boolean;
  /** Show upload queue */
  showQueue?: boolean;
  /** Callback when all uploads complete */
  onComplete?: (files: UploadFile[]) => void;
  /** Callback when file is removed */
  onRemove?: (fileId: string) => void;
}

/**
 * MultiFileUploader component - advanced file upload with progress tracking
 * Supports pause/resume, retry, and batch uploads
 */
export const MultiFileUploader: React.FC<MultiFileUploaderProps> = ({
  onUpload,
  accept,
  maxSize,
  maxFiles = 10,
  autoUpload = true,
  showQueue = true,
  onComplete,
  onRemove,
  ...boxProps
}) => {
  const [uploadQueue, setUploadQueue] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const generateId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const updateFile = useCallback((fileId: string, updates: Partial<UploadFile>) => {
    setUploadQueue((prev) => prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f)));
  }, []);

  const updateFileStatus = useCallback(
    (fileId: string, status: UploadFile['status']) => {
      updateFile(fileId, { status });
    },
    [updateFile]
  );

  const updateFileProgress = useCallback(
    (fileId: string, progress: number) => {
      updateFile(fileId, { progress });
    },
    [updateFile]
  );

  const startUpload = useCallback(
    async (filesToUpload?: UploadFile[]) => {
      const files = filesToUpload || uploadQueue.filter((f) => f.status === 'pending');
      if (files.length === 0) return;

      setIsUploading(true);

      for (const uploadFile of files) {
        if (uploadFile.status !== 'pending') continue;

        try {
          // Update status to uploading
          updateFileStatus(uploadFile.id, 'uploading');

          // Upload with progress tracking
          const result = await onUpload(uploadFile.file, (progress) => {
            updateFileProgress(uploadFile.id, progress);
          });

          // Update completed status
          updateFile(uploadFile.id, {
            status: 'completed',
            progress: 100,
            url: result.url,
          });
        } catch (error) {
          // Update error status
          updateFile(uploadFile.id, {
            status: 'error',
            error: error instanceof Error ? error.message : 'Upload failed',
          });
        }
      }

      setIsUploading(false);

      // Check if all uploads are complete
      const allFiles = uploadQueue.filter((f) => f.status === 'completed' || f.status === 'error');
      if (allFiles.length === uploadQueue.length) {
        onComplete?.(allFiles);
      }
    },
    [uploadQueue, onUpload, onComplete, updateFile, updateFileStatus, updateFileProgress]
  );

  const handleFileDrop = useCallback(
    (files: File[]) => {
      const newFiles: UploadFile[] = files.map((file) => ({
        id: generateId(),
        file,
        progress: 0,
        status: 'pending' as const,
      }));

      setUploadQueue((prev) => [...prev, ...newFiles]);

      if (autoUpload && !isUploading) {
        startUpload(newFiles);
      }
    },
    [autoUpload, isUploading, startUpload]
  );

  const pauseUpload = (fileId: string) => {
    updateFileStatus(fileId, 'paused');
    // In a real implementation, you would cancel the upload request
  };

  const resumeUpload = (fileId: string) => {
    const file = uploadQueue.find((f) => f.id === fileId);
    if (file) {
      startUpload([{ ...file, status: 'pending' }]);
    }
  };

  const retryUpload = (fileId: string) => {
    updateFile(fileId, { status: 'pending', progress: 0, error: undefined });
    const file = uploadQueue.find((f) => f.id === fileId);
    if (file) {
      startUpload([{ ...file, status: 'pending' }]);
    }
  };

  const removeFile = (fileId: string) => {
    setUploadQueue((prev) => prev.filter((f) => f.id !== fileId));
    onRemove?.(fileId);
  };

  const clearCompleted = () => {
    setUploadQueue((prev) => prev.filter((f) => f.status !== 'completed'));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircledIcon />;
      case 'error':
        return <CrossCircledIcon />;
      case 'uploading':
        return <UploadIcon />;
      case 'paused':
        return <PauseIcon />;
      default:
        return <FileIcon />;
    }
  };

  const getStatusColor = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'uploading':
        return 'info';
      case 'paused':
        return 'warning';
      default:
        return 'gray';
    }
  };

  const pendingCount = uploadQueue.filter((f) => f.status === 'pending').length;
  const completedCount = uploadQueue.filter((f) => f.status === 'completed').length;

  return (
    <Box {...boxProps}>
      <FileDropzone
        onDrop={handleFileDrop}
        accept={accept}
        maxSize={maxSize}
        maxFiles={maxFiles}
        showPreview={false}
      />

      {showQueue && uploadQueue.length > 0 && (
        <Box marginTop={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Text fontSize="sm" fontWeight="medium">
                Upload Queue
              </Text>
              <Badge size="sm">{uploadQueue.length}</Badge>
            </Box>

            <Box display="flex" gap={2}>
              {!autoUpload && pendingCount > 0 && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => startUpload()}
                  disabled={isUploading}
                >
                  Upload All ({pendingCount})
                </Button>
              )}
              {completedCount > 0 && (
                <Button size="sm" variant="ghost" onClick={clearCompleted}>
                  Clear Completed
                </Button>
              )}
            </Box>
          </Box>

          <Stack gap={2}>
            {uploadQueue.map((uploadFile) => (
              <Box
                key={uploadFile.id}
                padding={3}
                backgroundColor="bg.surface"
                borderRadius="md"
                border="1px solid"
                borderColor="border.muted"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  marginBottom={2}
                >
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <Icon color={`fg.${getStatusColor(uploadFile.status)}`}>
                      {getStatusIcon(uploadFile.status)}
                    </Icon>
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="medium" lineClamp={1}>
                        {uploadFile.file.name}
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        {formatFileSize(uploadFile.file.size)}
                      </Text>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    {uploadFile.status === 'uploading' && (
                      <Button size="xs" variant="ghost" onClick={() => pauseUpload(uploadFile.id)}>
                        <Icon>
                          <PauseIcon />
                        </Icon>
                      </Button>
                    )}
                    {uploadFile.status === 'paused' && (
                      <Button size="xs" variant="ghost" onClick={() => resumeUpload(uploadFile.id)}>
                        <Icon>
                          <PlayIcon />
                        </Icon>
                      </Button>
                    )}
                    {uploadFile.status === 'error' && (
                      <Button size="xs" variant="ghost" onClick={() => retryUpload(uploadFile.id)}>
                        <Icon>
                          <ReloadIcon />
                        </Icon>
                      </Button>
                    )}
                    <Button size="xs" variant="ghost" onClick={() => removeFile(uploadFile.id)}>
                      <Icon>
                        <Cross2Icon />
                      </Icon>
                    </Button>
                  </Box>
                </Box>

                {(uploadFile.status === 'uploading' || uploadFile.status === 'paused') && (
                  <Box>
                    <Progress value={uploadFile.progress} size="sm" />
                    <Text fontSize="xs" color="fg.muted" marginTop={1}>
                      {uploadFile.progress}% uploaded
                    </Text>
                  </Box>
                )}

                {uploadFile.status === 'error' && uploadFile.error && (
                  <Text fontSize="xs" color="fg.error" marginTop={1}>
                    {uploadFile.error}
                  </Text>
                )}

                {uploadFile.status === 'completed' && (
                  <Text fontSize="xs" color="fg.success" marginTop={1}>
                    Upload complete
                  </Text>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default MultiFileUploader;
